import { inject, Injectable } from '@angular/core';
import { FilterBy } from '@shared/sync/filter-by.model';
import { IPaginatedItemsModel } from '@shared/sync/paginated-items.model';
import { SortBy } from '@shared/sync/sort-by.model';
import { Task } from '../models/task.model';
import { TaskMapper } from '../mappers/task.mapper';
import { GetTaskInPaginationResponse } from '../models/get-task-in-pagination-response.model';
import { HsBlazorService } from '@hsetu/core-services';

@Injectable({
  providedIn: 'root',
})
export class OfferService {
  private readonly _serviceKey = 'kfmOffer';

  private readonly _blazorService = inject(HsBlazorService);
  private readonly _mapper = new TaskMapper();

  public async getAll(
    pageSize = 10,
    pageIndex = 0,
    sortBy: SortBy | null = null,
    filterBy: FilterBy | null = null,
    showAccomplished = true,
  ): Promise<IPaginatedItemsModel<Task>> {
    const offersResult = await this._blazorService.invokeService(
      this._serviceKey,
      'Get',
      {
        sortBy: sortBy,
        filterBy: filterBy,
        pageIndex: pageIndex,
        pageSize: pageSize,
        showAccomplished: showAccomplished,
      },
    );

    if (offersResult) {
      const result = (
        offersResult as Record<string, IPaginatedItemsModel<Task>>
      )['result'];

      result.data = result.data.map((offer) => this._mapper.taskMap(offer));

      return result;
    }

    return { count: 0, data: [], pageSize: 0, pageIndex: 0 };
  }

  public async getById(id: string): Promise<Task | undefined> {
    const offerResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetByIds',
      {
        ids: [id],
      },
    );

    if (offerResult) {
      const offers = (offerResult as Record<string, Task[]>)['offers'];

      if (offers && offers.length > 0) {
        return this._mapper.taskMap(offers[0]);
      }
    }

    return undefined;
  }

  public async getInPagination(
    offerId: string,
    from: Date,
    to: Date,
    pageSize = 10,
    showAccomplished = false,
    sortBy: SortBy | null = null,
  ): Promise<GetTaskInPaginationResponse | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'GetInPagination',
      {
        offerId: offerId,
        pageSize: pageSize,
        from: from,
        to: to,
        showAccomplished: showAccomplished,
        sortBy: sortBy,
      },
    );

    if (result) {
      const offers = result as Record<string, unknown>;
      const mappedData: Task[] = (offers['data'] as Task[]).map((task: Task) =>
        this._mapper.taskMap(task),
      );

      const paginationResponse: GetTaskInPaginationResponse = {
        pageIndex: offers['pageIndex'] as number,
        pageSize: offers['pageSize'] as number,
        count: offers['count'] as number,
        data: mappedData,
        from: new Date(String(offers['from'])),
        to: new Date(String(offers['to'])),
      };

      return paginationResponse;
    }

    return undefined;
  }

  public async create(customerId?: string): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Create',
      { customerId: customerId },
    );

    if (result) {
      return this._mapper.taskMap(
        (result as Record<string, unknown>)['offer'] as Task,
      );
    }

    return undefined;
  }

  public async createWithItems(
    items: { itemId: string; quantity: number }[],
  ): Promise<Task | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'CreateWithItems',
      { offerItems: items },
    );

    if (result) {
      return this._mapper.taskMap(
        result as Record<string, unknown>['offer'] as Task,
      );
    }

    return undefined;
  }

  public async update(value: Task): Promise<boolean> {
    const request = this._mapper.mapOfferRequest(value);

    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Update',
      request,
    );

    if (result) {
      return true;
    }

    return false;
  }

  public async delete(id: string): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Delete',
      { id: id },
    );

    return !!result && (result as Record<string, unknown>)['deleted'] === true;
  }
}
